import useSWR from 'swr';
import { useAuth } from '@/contexts/auth-context';

interface CachedTierReturn {
    tier: string | null;
    isLoadingTier: boolean;
    tierError: Error | null;
    mutateTier: () => Promise<any>;
}

/**
 * Cached hook for user tier data using SWR
 * Provides instant cached tier info on subsequent loads
 */
export function useCachedTier(): CachedTierReturn {
    const { user } = useAuth();

    const {
        data: tier,
        error: tierError,
        isLoading: isLoadingTier,
        mutate: mutateTier
    } = useSWR(
        user?.id ? ['user-tier', user.id] : null,
        async () => {
            const response = await fetch('/api/user/tier');
            if (!response.ok) {
                throw new Error('Failed to fetch tier');
            }
            const data = await response.json();
            return data.tier as string;
        },
        {
            revalidateOnFocus: true,
            dedupingInterval: 2000,
            focusThrottleInterval: 5000,
            // Fallback to 'Free' on error
            fallbackData: 'Free',
            onError: (error) => {
                console.error('Error fetching user tier:', error);
            }
        }
    );

    return {
        tier: tier ?? 'Free',
        isLoadingTier,
        tierError: tierError ?? null,
        mutateTier,
    };
}
